import { createSaveStorage } from './saveStorage.js';

const isWeChat = typeof wx !== 'undefined' && typeof wx.createCanvas === 'function';

export const canvas = isWeChat ? (globalThis.__islandCanvas || wx.createCanvas()) : document.querySelector('#game');
if (isWeChat) {
  canvas.style ||= {};
  canvas.addEventListener ||= () => {};
  canvas.removeEventListener ||= () => {};
  canvas.setAttribute ||= () => {};
}

const positive = (value, fallback) => Number.isFinite(value) && value > 0 ? value : fallback;
const inset = (value, max) => Math.max(0, Math.min(Number(value) || 0, max));
let safeAreaProbe;

export function viewport() {
  if (isWeChat) {
    let info;
    try { info = wx.getWindowInfo ? wx.getWindowInfo() : wx.getSystemInfoSync(); }
    catch {
      try { info = wx.getSystemInfoSync(); }
      catch { info = { windowWidth: 960, windowHeight: 540, pixelRatio: 1 }; }
    }
    const width = positive(info.windowWidth, 960);
    const height = positive(info.windowHeight, 540);
    const area = info.safeArea;
    const safeArea = {
      top: inset(area?.top, height / 3),
      right: inset(area ? width - area.right : 0, width / 3),
      bottom: inset(area ? height - area.bottom : 0, height / 3),
      left: inset(area?.left, width / 3),
    };
    // Reserve the capsule's entire top strip so no game controls sit underneath it.
    try {
      const menu = wx.getMenuButtonBoundingClientRect?.();
      if (menu?.width > 0 && menu.bottom > 0 && menu.bottom < height / 3) safeArea.top = Math.max(safeArea.top, menu.bottom + 8);
    } catch { /* Older clients may not expose the capsule geometry. */ }
    let lowEnd = false;
    try { const level = wx.getDeviceInfo?.().benchmarkLevel ?? -1; lowEnd = level >= 0 && level <= 10; } catch {}
    return { width, height, dpr: Math.min(positive(info.pixelRatio, 1), lowEnd ? 1 : 1.5), safeArea, isTouch: true, isWeChat: true };
  }
  const width = positive(globalThis.visualViewport?.width, globalThis.innerWidth || 960);
  const height = positive(globalThis.visualViewport?.height, globalThis.innerHeight || 540);
  const isTouch = (globalThis.navigator?.maxTouchPoints || 0) > 0 || !!globalThis.matchMedia?.('(pointer: coarse)').matches;
  const safeArea = { top: 0, right: 0, bottom: 0, left: 0 };
  if (document.body && globalThis.getComputedStyle) {
    if (!safeAreaProbe) {
      safeAreaProbe = document.createElement('div');
      safeAreaProbe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top);padding-right:env(safe-area-inset-right);padding-bottom:env(safe-area-inset-bottom);padding-left:env(safe-area-inset-left)';
      document.body.appendChild(safeAreaProbe);
    }
    const styles = getComputedStyle(safeAreaProbe);
    for (const edge of Object.keys(safeArea)) safeArea[edge] = inset(parseFloat(styles[`padding${edge[0].toUpperCase()}${edge.slice(1)}`]), (edge === 'top' || edge === 'bottom' ? height : width) / 3);
  }
  return { width, height, dpr: Math.min(positive(globalThis.devicePixelRatio, 1), isTouch ? 1.5 : 1.75), safeArea, isTouch, isWeChat: false };
}

export function offscreen(width, height) {
  const result = isWeChat && wx.createOffscreenCanvas
    ? wx.createOffscreenCanvas({ type: '2d', width, height })
    : isWeChat ? wx.createCanvas() : document.createElement('canvas');
  result.width = width;
  result.height = height;
  return result;
}

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = isWeChat ? wx.createImage() : new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    if (isWeChat) {
      const filename = src.split('/').pop().replace(/[^a-zA-Z0-9_.-]/g, '_');
      const path = `${wx.env.USER_DATA_PATH}/island-${filename}`;
      try {
        const fs = wx.getFileSystemManager();
        fs.writeFileSync(path, fs.readFileSync(src, 'base64'), 'base64');
      }
      catch (error) { reject(error); return; }
      image.src = path;
      return;
    }
    image.src = src;
  });
}

let browserSaveStorage;
export function getBrowserSaveStorage() {
  if (isWeChat) return null;
  return browserSaveStorage ||= createSaveStorage(localStorage);
}

export function loadState() {
  try {
    if (!isWeChat) return getBrowserSaveStorage().load();
    const value = wx.getStorageSync('island-state');
    return typeof value === 'string' ? JSON.parse(value) : value;
  } catch { return null; }
}

let localSavesSuspended = false;
export function suspendLocalSaves() { localSavesSuspended = true; }

export function saveState(value) {
  if (localSavesSuspended) return;
  try {
    const text = JSON.stringify(value);
    if (isWeChat) wx.setStorageSync('island-state', text);
    else getBrowserSaveStorage().save(value);
  } catch { /* Storage failure must not stop play. */ }
}

export function onResize(handler) {
  if (isWeChat && wx.onWindowResize) wx.onWindowResize(handler);
  else {
    addEventListener('resize', handler);
    globalThis.visualViewport?.addEventListener('resize', handler);
  }
}

export function onForegroundChange(handler) {
  if (isWeChat) {
    wx.onShow?.(() => handler(true));
    wx.onHide?.(() => handler(false));
    return;
  }
  document.addEventListener('visibilitychange', () => handler(document.visibilityState === 'visible'));
  addEventListener('pagehide', () => handler(false));
  addEventListener('pageshow', () => handler(document.visibilityState === 'visible'));
}

export function onTouches({ start, move, end, cancel = () => {} }) {
  if (isWeChat) {
    let active = false;
    const points = (event) => [...(event.touches || [])].map((touch) => ({
      id: touch.identifier, x: touch.clientX ?? touch.x, y: touch.clientY ?? touch.y,
    }));
    wx.onTouchStart((event) => { const next = points(event); active = next.length > 0; start(next); });
    wx.onTouchMove((event) => { if (active) move(points(event)); });
    wx.onTouchEnd((event) => {
      if (!active) return;
      const remaining = points(event);
      if (event.changedTouches?.length) move([...remaining, ...points({ touches: event.changedTouches })]);
      active = remaining.length > 0;
      end(remaining);
    });
    const abort = () => { active = false; cancel([]); };
    wx.onTouchCancel(abort);
    wx.onHide?.(abort);
    return;
  }
  const active = new Map();
  const points = () => [...active.values()];
  const pointerPoint = (event) => {
    const bounds = canvas.getBoundingClientRect();
    const view = viewport();
    return { id: event.pointerId, x: (event.clientX - bounds.left) * view.width / (bounds.width || view.width), y: (event.clientY - bounds.top) * view.height / (bounds.height || view.height) };
  };
  canvas.addEventListener('pointerdown', (event) => {
    if (event.button != null && event.button !== 0) return;
    // Document listeners also cover clients that cannot capture the pointer.
    try { canvas.setPointerCapture?.(event.pointerId); } catch { /* Continue with document-level delivery. */ }
    active.set(event.pointerId, pointerPoint(event));
    start(points());
  });
  document.addEventListener('pointermove', (event) => {
    if (!active.has(event.pointerId)) return;
    active.set(event.pointerId, pointerPoint(event));
    move(points());
  }, { capture: true });
  const finish = (event) => {
    if (!active.has(event.pointerId) || (event.button != null && event.button !== 0)) return;
    active.set(event.pointerId, pointerPoint(event));
    // The last pointermove may precede pointerup by many pixels.
    move(points());
    active.delete(event.pointerId);
    // A completed press/release activates once even if the later browser click
    // is retargeted, suppressed by an embedded web view, or followed by blur.
    end(points(), { pointerType: event.pointerType || 'mouse' });
  };
  const abort = () => {
    if (!active.size) return;
    active.clear();
    cancel([]);
  };
  document.addEventListener('pointerup', finish, { capture: true });
  document.addEventListener('pointercancel', (event) => { if (active.has(event.pointerId)) abort(); }, { capture: true });
  canvas.addEventListener('lostpointercapture', (event) => { if (active.has(event.pointerId)) abort(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState !== 'visible') abort(); });
  addEventListener('pagehide', abort);
  addEventListener('blur', abort);
}

export function nextFrame(callback) {
  const raf = canvas.requestAnimationFrame?.bind(canvas) || globalThis.requestAnimationFrame;
  return raf ? raf(callback) : setTimeout(() => callback(Date.now()), 16);
}

export { isWeChat };
